import React from 'react';
import { useTranslation } from 'react-i18next';

import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	TextField,
	Typography,
} from '@mui/material';

import { BOOKING_DEADLINE_HOURS } from '@common/constants/academyReworkings';
import { parseDate, teacherName } from '@common/helpers/academyReworkings';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { nextButtonSx, twoColumnsSx } from '../../styled';

export default function Confirmation({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	const items = [...flow.booked, ...flow.pendingAssignments].filter(
		(item, index, all) =>
			all.findIndex((other) => other.lesson.gradebook_id === item.lesson.gradebook_id) === index,
	);
	return (
		<Box display="flex" flexDirection="column" gap="20px">
			<Typography variant="h3">{t('academyReworkings.confirmTitle')}</Typography>
			{flow.booked.length > 0 && (
				<Alert severity="info">
					{t('academyReworkings.partiallyBooked', { count: flow.booked.length })}
				</Alert>
			)}
			<Box sx={twoColumnsSx}>
				{items.map(({ lesson, slot }) => (
					<Box key={lesson.gradebook_id} sx={{ minWidth: 0 }}>
						<Paper sx={{ p: 2, minWidth: 0, borderRadius: '16px', height: '100%' }}>
							<Typography color="text.secondary" variant="body2">
								{parseDate(slot.start_dt).toFormat('ccc, dd.MM.yyyy · HH:mm')}–
								{parseDate(slot.end_dt).toFormat('HH:mm')}
							</Typography>
							<Typography fontWeight={600} sx={{ my: 1 }}>
								{lesson.lesson_name}
							</Typography>
							<Typography>{teacherName(slot.teacher)}</Typography>
							{flow.booked.some((item) => item.lesson.gradebook_id === lesson.gradebook_id) && (
								<Chip
									size="small"
									color="success"
									label={t('academyReworkings.booked')}
									sx={{ mt: 1 }}
								/>
							)}
						</Paper>
					</Box>
				))}
			</Box>
				<Typography variant="body2" color="text.secondary">
					{t('academyReworkings.contactHint')}
				</Typography>
				<Box sx={twoColumnsSx}>
					<Box sx={{ minWidth: 0 }}>
						<TextField
							fullWidth
							label={t('academyReworkings.phone')}
							value={flow.phone}
							onChange={(event) => flow.setPhone(event.target.value)}
							disabled={flow.busy}
							type="tel"
						/>
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<TextField
							fullWidth
							label={t('academyReworkings.comment')}
							value={flow.comment}
							onChange={(event) => flow.setComment(event.target.value)}
							disabled={flow.busy}
							multiline
							maxRows={3}
						/>
					</Box>
				</Box>
				<Alert severity="info">
					{t('academyReworkings.bookingDeadline', { hours: BOOKING_DEADLINE_HOURS })}
				</Alert>
				<Typography variant="body2" color="text.secondary">
					{t('academyReworkings.timeZone')}
				</Typography>
				<Box display="flex" justifyContent="flex-end">
					<Button
						sx={nextButtonSx}
						variant="contained"
						disabled={flow.busy}
						startIcon={flow.busy ? <CircularProgress size={18} /> : undefined}
						onClick={flow.confirmBooking}
					>
						{t('academyReworkings.confirmBooking')}
					</Button>
				</Box>
		</Box>
	);
}
